import { useEffect } from 'react';

import { logError } from '.';
import { kebabToCamel } from '../Components/KebabToCamel';
import { FeatureFlagItem } from '../Services/ApiResponseModels';
import { WebApiEndpoints } from '../Services/Endpoints';
import RestService from '../Services/RestService';

declare const Spotlight: any;

// *** PRACTICE / GIFTBOX FEATURE FLAGS ***

// Flags that have been removed and are now hard-coded to a terminal state.
// See Features.cs, Spotlight.Core.Common.FeatureFlag
const removedFlags: Array<FeatureFlagItem> = [
    {
        value: 256,
        name: 'MYOBImportV2',
        isEnabled: true,
    },
    {
        value: 1024,
        name: 'MYOBClearStagingSP',
        isEnabled: true,
    },
    {
        value: 4096,
        name: 'MYOBSpotlightAPI',
        isEnabled: true,
    },
    {
        value: 8192,
        name: 'MyobImportPipeline',
        isEnabled: false,
    },
];

/**
 * Checks if the practice has the given feature flag.
 * Relies on the featureflags being assigned to the global Spotlight.PracticeFeatureFlags.
 * Prefer to instead call asyncPracticeHasFeatureFlag for new code that is safe to operate async.
 */
// TODO: Restrict the type of the flag parameter further so that the value must be in the FeatureFlags enum
export const practiceHasFeatureFlag = (flag: string | FeatureFlags): boolean => {
    const flags = typeof Spotlight !== 'undefined' && Spotlight.PracticeFeatureFlags;
    if (flags === undefined || flags === '') {
        // Note: if the practice has no features toggled on we should at least receive the string 'None' here
        logError({
            error: new Error(
                `Attempted to check practice feature flags but flags were not set. Feature Flag to check was ${flag}.`,
            ),
        });
        return false;
    }

    const removedNames = new Set(removedFlags.map((flag) => flag.name.toLowerCase()));
    const alwaysOn = removedFlags.filter((flag) => flag.isEnabled).map((flag) => flag.name.toLowerCase());

    return (
        flags
            .split('|')
            .map((flag) => flag.trim().toLowerCase())
            .filter((flag) => !removedNames.has(flag))
            .concat(alwaysOn)
            .indexOf(flag.toLowerCase()) >= 0
    );
};

/**
 * Checks if the practice has the given feature flag.
 * Currently just checks the flag from the Spotlight global var.
 * Intent would be for this to fetch featureflags from the API
 * possibly memoize/cache and remove the dependency on the Spotlight global var.
 */
// TODO: Restrict the type of the flag property further so that the value must be in the FeatureFlags enum
export const asyncPracticeHasFeatureFlag = async ({ flag }: { flag: string | FeatureFlags }): Promise<boolean> => {
    return practiceHasFeatureFlag(flag);
};

// Helper function for code consuming feature flags to permit hard-coding some flags as on or off
// once those flags are no longer configurable and have been removed from most of the codebase
// (but are potentially still used in e.g.the Desktop Connector)
export const getGiftboxFeatureFlags = async (): Promise<FeatureFlagItem[]> => {
    let flags: FeatureFlagItem[];

    const windowFFs = window.Spotlight.PracticeFeatureFlags;

    if (windowFFs?.length) {
        flags = windowFFs.split('|').map((flag: string) => ({
            value: 0,
            name: flag,
            isEnabled: true,
        }));
    } else {
        try {
            const response = await RestService.performWebApiCallAsync<FeatureFlagItem[]>({
                url: WebApiEndpoints.PracticeFeatureFlags(),
                method: 'GET',
            });

            flags = response.data;
        } catch {
            flags = [];
        }
    }

    return [...flags.filter((f) => !removedFlags.some((r) => r.name === f.name)), ...removedFlags];
};

// *** LAUNCH DARKLY FEATURE FLAGS ***

export enum FeatureFlags {
    NoThumbnails = 'no-thumbnails',
    CustomisePageBuildWithPolling = 'customise-page-build-with-polling',
    KarbonIntegration = 'karbon-integration',
    PreviewBuildWithPolling = 'preview-build-with-polling',
    XeroImportUsingTrackingOptions = 'import-tracking-data-of-active-options-only',
    MultiActionPlanAi = 'release-multi-action-plan-ai',
    ForecastingActionPlanAi = 'release-forecasting-action-plan-ai',
    SwitchEditableOrgFieldSFintoSR = 'switch-editable-org-field-sf-into-sr',
    UseMyobNewApiScope = 'switch-myob-use-new-api-scope',
    MigrationPerplexityAIJobsRefactor = 'migration-perplexity-ai-jobs-refactor',
    Digits = 'release-integration-digits',
    AskSpotlight = 'release-ai-ask-spotlight',
    ForecastingImportUxUpdate = 'release-forecasting-import-ux-update',
    EditWithAiUndefinedTagsFix = 'switch-edit-with-ai-undefined-tags-fix',
}

// Ref: https://support.pendo.io/hc/en-us/articles/360032294291-Configure-Track-Events
const MAX_PENDO_KEY_LENGTH = 32; // Pendo only supports property keys up to 32 characters
const PENDO_BATCH_SIZE = 15; // Pendo recommends keeping event property count low, so we batch them

export const FeatureFlagMetrics: React.FC = () => {
    useEffect(() => {
        let runCount = 0;
        const intervalId = window.setInterval(async () => {
            runCount++;
            if (
                typeof window.rg4js === 'function' &&
                typeof window.pendo !== 'undefined' &&
                typeof window.pendo.isReady === 'function' &&
                window.pendo.isReady()
            ) {
                try {
                    const allFFValues = (await RestService.GetAllFeatureFlagValues())?.data;
                    const raygunFeatureFlagTagList: string[] = [];
                    const pendoFeatureFlagList: Record<string, boolean> = {};

                    if (allFFValues) {
                        Object.entries(allFFValues).forEach(([key, value]) => {
                            value && raygunFeatureFlagTagList.push(key);
                            pendoFeatureFlagList[kebabToCamel(key).slice(0, MAX_PENDO_KEY_LENGTH)] = value;
                        });
                        // Add feature flags as tags to all Raygun events
                        window.rg4js('withTags', raygunFeatureFlagTagList);

                        // Pendo tracking event
                        for (let i = 0; i < Object.entries(pendoFeatureFlagList).length; i += PENDO_BATCH_SIZE) {
                            const batch = Object.fromEntries(
                                Object.entries(pendoFeatureFlagList).slice(i, i + PENDO_BATCH_SIZE),
                            );
                            pendo.track('Launch Darkly - Feature Flags', batch);
                        }
                    }
                } catch {
                    logError({ error: new Error('Error fetching all feature flag values') });
                } finally {
                    window.clearInterval(intervalId);
                }
            } else if (runCount >= 10) {
                window.clearInterval(intervalId);
            }
        }, 500);

        return () => window.clearInterval(intervalId);
    }, []);

    return null;
};
